import React from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { 
  formatYAxisValue, 
  formatHorizontalBarLabel, 
  getHorizontalBarChartConfig,
  formatCurrency 
} from '../../Components/DrillDown/formatters';
import { HorizontalBarChartSkeleton } from '../../Components/Skeletons/ChartSkeletons';
import {
  Card,
  CardContent,
  Typography,
  Box,
  useTheme,
  Grid
} from '@mui/material';
import GenericModal from '../../Components/GenericModal';
import {
  DrillDownProductInfo,
  DrillDownEvolutionChart,
  DrillDownCfopChart,
  DrillDownDetailedTransactions,
  DrillDownLossesInfo,
  DrillDownComparativeAnalysis
} from '../../Components/DrillDown';
import { DrillDownPerdasData } from './types';

type DebitoIcmsChartData = {
  produto: string;
  ICMS: number;
  cod_item?: string;
};

type Props = {
  data: DebitoIcmsChartData[];
  title: string;
  barColor?: string;
  loading?: boolean;
  spedFiscalId?: number;
};

const BarChartIcmsPorProduto: React.FC<Props> = ({
  data,
  title,
  barColor,
  loading = false,
  spedFiscalId
}) => {
  const theme = useTheme();
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [selectedProduct, setSelectedProduct] = React.useState<string | null>(null);
  const [drillDownData, setDrillDownData] = React.useState<DrillDownPerdasData | null>(null);
  const [loadingDrillDown, setLoadingDrillDown] = React.useState(false);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const handleBarClick = async (clickData: any) => {
    if (!spedFiscalId) return;
    
    const barData = clickData.data as DebitoIcmsChartData;
    const produtoCompleto = (barData as any).produtoCompleto || barData.produto; // ex: "12345 - Produto A"
    
    // Extrair código do produto (primeiro elemento antes do " - ")
    const codItemMatch = produtoCompleto.match(/^(\S+)/);
    if (!codItemMatch) {
      console.error('Código do produto não encontrado para:', produtoCompleto);
      return;
    }
    
    const codItem = codItemMatch[1];
    setSelectedProduct(produtoCompleto);
    setIsModalOpen(true);
    setLoadingDrillDown(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}perdas-produto-detalhes-drill-down/${spedFiscalId}/${codItem}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const detailData: DrillDownPerdasData = await response.json();
      
      if (detailData && typeof detailData === 'object') {
        setDrillDownData(detailData);
      } else {
        console.error('Estrutura de dados inválida:', detailData);
      }
    } catch (error) {
      console.error('Erro ao buscar detalhes das perdas do produto:', error);
    } finally {
      setLoadingDrillDown(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
    setDrillDownData(null);
    setPage(0);
  };

  React.useEffect(() => {
    setPage(0);
  }, [drillDownData]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL"
    }).format(value);
  };

  const formatNumber = (value: number | string) => {
    const numValue = Number(value);
    if (isNaN(numValue)) return '0';
    return numValue.toLocaleString('pt-BR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  };

  const formatQuantity = (value: number | string) => {
    const numValue = Number(value);
    if (isNaN(numValue)) return '0,00';
    return numValue.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const formatMonthYear = (mes: number, ano: number) => {
    const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
                       'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    return `${monthNames[mes - 1]}/${ano}`;
  };

  const renderDetailView = () => {
    if (!drillDownData) return null;

    const { produto, ncm_info, perdas_por_mes, cfops_produto, transacoes_perdas, analise_comparativa } = drillDownData;

    if (!produto) return null;

    // Adaptar dados do produto de perdas para o formato esperado pelo DrillDownProductInfo
    const adaptedProductData = {
      descr_compl: produto.descr_compl || 'Produto não encontrado',
      cod_item: produto.cod_item || 'N/A',
      total_debito_icms: produto.total_debito_icms || 0,
      total_credito_icms: produto.total_credito_icms || 0,
      quantidade_entrada: produto.quantidade_entrada || 0,
      quantidade_saida: produto.quantidade_saida || 0,
      quantidade_total: produto.quantidade_total || 0,
      total_transacoes: produto.total_transacoes || 0,
      valor_total_item: produto.valor_total_item || 0
    };

    // Adaptar dados específicos das perdas para o componente DrillDownLossesInfo
    const adaptedLossesData = {
      valor_total_perdas: produto.valor_total_perdas || 0,
      icms_total_perdas: produto.icms_total_perdas || 0,
      total_transacoes_perdas: produto.total_transacoes_perdas || 0,
      quantidade_total_perdas: produto.quantidade_total_perdas || 0,
      percentual_perdas: produto.percentual_perdas || 0,
      preco_medio_perdas: produto.preco_medio_perdas || 0,
      ncm_info: ncm_info || undefined
    };

    // Adaptar dados de evolução das perdas para o formato esperado pelo DrillDownEvolutionChart
    const adaptedEvolutionData = (perdas_por_mes || []).map(item => {
      
      return {
        mes: item.mes,
        ano: item.ano,
        valor_perdas: item.valor_perdas,
        valor_transacoes_perdas: item.valor_transacoes_perdas
      };
    });

    const lineData = [
      {
        id: 'Valor Perdas',
        data: adaptedEvolutionData.map(item => ({
          x: formatMonthYear(item.mes, item.ano),
          y: item.valor_perdas || 0,
          transacoes: item.valor_transacoes_perdas || 0
        }))
      }
    ];

    // Adaptar dados de CFOP para o formato esperado pelo DrillDownCfopChart
    const adaptedCfopData = (cfops_produto || []).map(item => ({
      cfop: item.cfop,
      total_icms: Number(item.total_icms) || 0,
      total_transacoes: Number(item.total_transacoes) || 0
    }));

    // Adaptar dados de transações detalhadas para o formato esperado pelo DrillDownDetailedTransactions
    const adaptedTransactionsData = (transacoes_perdas || []).map(item => ({
      cfop: item.cfop,
      dt_emissao: item.dt_emissao,
      num_doc: item.num_doc,
      vl_icms: item.vl_icms,
      qtd: item.qtd,
      vl_item: item.vl_item,
      ind_oper: '1' // Perdas são sempre saídas
    }));

    return (
      <Grid container spacing={0.5}>
        <DrillDownProductInfo produto={adaptedProductData} />
        
        <DrillDownLossesInfo 
          lossesData={adaptedLossesData}
          title="Informações Específicas das Perdas"
        />
        
        <DrillDownEvolutionChart 
          lineData={lineData}
          title="Evolução Mensal das Perdas"
        />
        
        <DrillDownCfopChart 
          top_cfops={adaptedCfopData}
          title="Análise por CFOP - Perdas"
        />
        
        {analise_comparativa && analise_comparativa.length > 0 && (
          <DrillDownComparativeAnalysis 
            analise_comparativa={analise_comparativa}
            title="Análise Comparativa - Perdas vs Outras Operações"
          />
        )}
        
        <DrillDownDetailedTransactions 
          transacoes_detalhadas={adaptedTransactionsData}
          title="Transações de Perdas Detalhadas"
        />
      </Grid>
    );
  };

  // Processar dados para incluir apenas TOP 10 e versões truncadas dos nomes
  const processedData = React.useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }

    // Ordenar dados por valor em ordem decrescente (maiores perdas primeiro)
    const sortedData = [...data].sort((a, b) => b.ICMS - a.ICMS);

    // Pegar apenas os TOP 10
    const top10 = sortedData.slice(0, 10).reverse();

    return top10.map(item => ({
      ...item,
      produtoCompleto: item.produto, // Nome completo para tooltip
      produto: formatHorizontalBarLabel(item.produto, 30), // Nome truncado para display
    }));
  }, [data]);

  const renderListView = () => {
    const chartConfig = getHorizontalBarChartConfig(processedData.length, true);

    return (
      <Box>
        <Typography
          variant="subtitle1"
          align="center"
          sx={{ color: theme.palette.text.secondary, mb: 2 }}
        >
          {title}
        </Typography>

        <Box sx={{ 
          height: chartConfig.height - 50,
          overflow: 'visible', // Permite tooltip fora dos limites
          position: 'relative',
          zIndex: 1 // Garante que tooltips fiquem acima
        }}>
          <ResponsiveBar
            valueFormat={formatCurrency}
            data={processedData}
            keys={['ICMS']}
            indexBy="produto"
            layout="horizontal"
            margin={chartConfig.margin}
            padding={chartConfig.padding}
            innerPadding={chartConfig.innerPadding}
            colors={barColor ? () => barColor : theme.palette.error.main}
            borderColor={{ from: 'color', modifiers: [['darker', 1.4]] }}
            axisTop={null}
            axisRight={null}
            axisBottom={chartConfig.axisBottom}
            axisLeft={chartConfig.axisLeft}
            labelSkipWidth={12}
            labelSkipHeight={12}
            labelTextColor="#ffffff"
            labelFormat={(value) =>
              `R$ ${Number(value).toLocaleString('pt-BR', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              })}`
            }
            theme={{
              labels: {
                text: {
                  fontSize: 11,
                  fontWeight: 600
                }
              },
              axis: {
                ticks: {
                  text: {
                    fontSize: 12
                  }
                }
              }
            }}
            tooltip={({ id, value, data: itemData }) => (
              <Box
                sx={{
                  background: theme.palette.background.paper,

                  p: 2,
                  minWidth: 280,
                  maxWidth: 400,
                  border: `1px solid ${theme.palette.divider}`
                }}
              >
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  📦 {(itemData as any).produtoCompleto || id}
                </Typography>
                <Typography variant="h6" color="error.main" sx={{ mb: 1 }}>
                  {formatCurrency(value as number)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Perdas de ICMS
                </Typography>
                {spedFiscalId && (
                  <Typography variant="caption" color="primary.main" sx={{ display: 'block', mt: 1, fontWeight: 500 }}>
                    💡 Clique para análise completa das perdas
                  </Typography>
                )}
              </Box>
            )}
            onClick={spedFiscalId ? handleBarClick : undefined}
            animate
            motionConfig="gentle"
            legends={[chartConfig.legends]}
          />
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Card
        sx={{
          p: 2,
          height: 600,
          overflow: 'visible' // Permite tooltip fora dos limites
        }}
      >
        <CardContent sx={{ p: 0, overflow: 'visible' }}>
          {loading ? (
            <Box
              sx={{
                height: 550,
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative'
              }}
            >
              <HorizontalBarChartSkeleton height={550} showTitle={false} />
            </Box>
          ) : (
            renderListView()
          )}
        </CardContent>
      </Card>

      <GenericModal
        open={isModalOpen}
        onClose={handleCloseModal}
        title="Detalhes das Perdas do Produto"
        loading={loadingDrillDown}
        showBreadcrumb={true}
        breadcrumbItems={[{ label: selectedProduct || 'Produto' }]}
        maxWidth="xl"
        height="90vh"
      >
        {renderDetailView()}
      </GenericModal>
    </>
  );
};

export default BarChartIcmsPorProduto;
